import React, {
  createContext,
  ReactNode,
  FC,
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
} from "react";
import useWeb3Auth from "../hooks/Web3Auth";
import { Web3Auth } from "@web3auth/modal";
import GetStarted from "../components/stepper/get-started/Component";
//import BillingAddressForm from "../components/stepper/billing-address-form/Component";
//import ShippingAddressForm from "../components/stepper/shipping-address-form/Component";
import SignIn from "../components/stepper/sign-in/Component";
//import KycForm from "../components/stepper/kyc-form/Component";
//import KYC from "../components/kyc/Component";
//import PaymentMethods from "../components/stepper/payment-methods/Component";
//import CreditCardPayment from "../components/stepper/credit-card-payment/Component";
//import CryptoPayment from "../components/stepper/cripto-payment/Component";
import theme from "../utils/theme";
//import SoldOut from "../components/sold-out/Component";
//import Offers from "../components/stepper/offers/Component";
import { OfferModel } from "../models/offer";
import Wallet from "../components/stepper/wallet/Component";
//import PhysicalItemSizes from "../components/stepper/physical-item-sizes/Component";

export interface StepModel {
  component: React.ReactElement;
}

export enum ContainerMaxWidth {
  DEFAULT = 716,
  MIDDLE = 1092,
  FULL = theme.breakpoints.values.lg,
  WALLET_VIEW = 1600,
}

export interface ActiveStepModel extends StepModel {
  title: "Get Started" | "Sign In" | "Wallet";
}

interface StepsModel {
  getStarted: StepModel;
  signIn: StepModel;
  wallet: StepModel;
  /* offers: StepModel;
  checkout: StepModel;
  billingAddressForm: StepModel;
  shippingAddressForm: StepModel;
  kycForm: StepModel;
  kyc: StepModel;
  creditCardPayment: StepModel;
  cryptoPayment: StepModel;
  soldOut: StepModel;
  physicalItemSizes: StepModel; */
}

interface FlowControlModel {
  activeStep: ActiveStepModel;
  steps: StepsModel;
  backStep: ActiveStepModel | undefined;
  highlightedSteps: string[];
}

export interface StoreContextModel {
  store: StoreModel;
  setStore: Dispatch<SetStateAction<StoreModel>>;
  setTransactionInProgress: (value: boolean) => void;
  setThreeDSInProgress: (value: boolean) => void;
  logout: () => Promise<void>;
}

export interface Address {
  firstName: string;
  lastName: string;
  country: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface BillingAddress extends Address {
  email: string;
  confirmMailingAddress: string;
}

export interface ShippingAddress extends Address {
  apartment: string;
  phone: string;
}

interface User {
  wallet: string;
  email: string;
  mintedCards: number;
}

export interface KYCForm {
  email: string;
  emailConfirmation: string;
  postalCode: string;
}

export interface CartModel {
  offer: OfferModel | null;
  kycForm: KYCForm;
  addresses: {
    billing: BillingAddress;
    shipping: ShippingAddress;
  };
  price: {
    subTotal: number;
    tax?: number;
    currency: "$" | "WETH";
  };
  paymentMethod: "CreditCard" | "Crypto";
  termsAccepted: boolean | null;
  user: User;
  isTransactionInProgress: boolean;
  threeDSInProgress: boolean;
  polygonScanHash: string | null;
  creditCardTransactionErrorMessage: string | null;
}

export interface StoreModel {
  web3Auth: {
    web3AuthInstance: Web3Auth | null;
    web3AuthProvider: any;
    isInitialized: boolean;
  };
  flowControl: FlowControlModel;
  containerMaxWidth: number;
  cart: CartModel;
}

export const StoreContext = createContext<StoreContextModel>({} as StoreContextModel);

type Props = {
  children: ReactNode;
};

// Web3Auth persists its session in localStorage under this key; cachedConnector is set while logged in.
const hasWeb3AuthSession = (): boolean => {
  try {
    const state = JSON.parse(localStorage.getItem("Web3Auth-state") ?? "null");
    return Boolean(state?.cachedConnector);
  } catch {
    return false;
  }
};

const initializeActiveStep = (): ActiveStepModel => {
  // With an existing session the Sign In step reconnects silently and moves on to the Wallet.
  if (localStorage.getItem("signIn") || hasWeb3AuthSession()) {
    return {
      title: "Sign In",
      component: <SignIn />,
    };
  }

  return {
    title: "Get Started",
    component: <GetStarted />,
  };
};

export const initialState: StoreModel = {
  web3Auth: {
    web3AuthInstance: null,
    web3AuthProvider: null,
    isInitialized: false,
  },
  flowControl: {
    activeStep: initializeActiveStep(),
    highlightedSteps: ["Get Started", "Sign In", "Wallet"],
    steps: {
      getStarted: {
        component: <GetStarted />,
      },
      signIn: {
        component: <SignIn />,
      },
      wallet: {
        component: <Wallet />,
      },
      /* offers: {
        component: <Offers />,
      },
      checkout: {
        component: <GetStarted />,
      }, */
      /*  billingAddressForm: {
        component: <BillingAddressForm />,
      }, */
      /*  shippingAddressForm: {
        component: <ShippingAddressForm />,
      }, */
      /* physicalItemSizes: {
        component: <PhysicalItemSizes />,
      },
      kycForm: {
        component: <KycForm />,
      },
      kyc: {
        component: <KYC />,
      },
      creditCardPayment: {
        component: <CreditCardPayment />,
      },
      cryptoPayment: {
        component: <CryptoPayment />,
      },
      soldOut: {
        component: <SoldOut />,
      }, */
    },
    backStep: undefined,
  },
  containerMaxWidth: ContainerMaxWidth.DEFAULT,
  cart: {
    kycForm: {
      email: "",
      emailConfirmation: "",
      postalCode: "",
    },
    offer: null,
    addresses: {
      billing: {},
      shipping: {},
    },
    user: {
      wallet: "",
      email: "",
      mintedCards: 0,
    },
    price: {
      subTotal: 99.0,
      tax: 7.99,
      currency: "$",
    },
    isTransactionInProgress: false,
    threeDSInProgress: false,
    termsAccepted: null,
    polygonScanHash: null,
    creditCardTransactionErrorMessage: null,
  } as CartModel,
};

export const StoreProvider: FC<Props> = ({ children }) => {
  const [store, setStore] = useState<StoreModel>(initialState);
  const { initialize } = useWeb3Auth();

  useEffect(() => {
    if (store.flowControl.activeStep.title === "Sign In") {
      const initWeb3Auth = async () => {
        try {
          const web3AuthInstance = await initialize();
          setStore((prevStore) => ({
            ...prevStore,
            web3Auth: {
              ...prevStore.web3Auth,
              web3AuthInstance: web3AuthInstance,
              isInitialized: true,
            },
          }));
        } catch (error) {
          console.error("Error initializing Web3Auth:", error);
        }
      };
      initWeb3Auth();
    }
  }, [initialize, store.flowControl.activeStep]);

  const setTransactionInProgress = (value: boolean) => {
    setStore((prevState) => ({
      ...prevState,
      cart: {
        ...prevState.cart,
        isTransactionInProgress: value,
      },
    }));
  };

  const setThreeDSInProgress = (value: boolean) => {
    setStore((prevState) => ({
      ...prevState,
      cart: {
        ...prevState.cart,
        threeDSInProgress: value,
      },
    }));
  };

  const logout = async () => {
    try {
      if (store.web3Auth.web3AuthInstance?.connected) {
        await store.web3Auth.web3AuthInstance.logout();
      }
    } catch (error) {
      console.error("Error logging out of Web3Auth:", error);
      // Drop the persisted session anyway, otherwise the next page load would sign the user back in.
      localStorage.removeItem("Web3Auth-state");
    }
    localStorage.removeItem("signIn");

    // A fresh Web3Auth instance is created when the user reaches the Sign In step again.
    setStore((prevState) => ({
      ...prevState,
      web3Auth: initialState.web3Auth,
      flowControl: {
        ...prevState.flowControl,
        activeStep: {
          ...prevState.flowControl.steps.getStarted,
          title: "Get Started",
        },
        backStep: undefined,
      },
      containerMaxWidth: ContainerMaxWidth.DEFAULT,
      cart: {
        ...prevState.cart,
        user: initialState.cart.user,
      },
    }));
  };

  return (
    <StoreContext.Provider
      value={{ store, setStore, setTransactionInProgress, setThreeDSInProgress, logout }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export default StoreProvider;
